import { useContext, useState } from "react"

import { 
  DataTable, 
  EllipsisDropdown, 
  EmptyPlaceholder, 
  EventPageHeader, 
  EventMenu,
  EventViewsTracker, 
  Page 
} from "@components"

import { UserContext } from "@context"

import { 
  GET_AUDIT_LOG, 
  GET_AUDIT_LOG_DOWNLOAD, 
  GET_AUDIT_LOG_SUMMARY
} from "@graphql"

import { 
  downloadFile, 
  mySqlToLocal, 
  showLoader 
} from "@helpers"

import { 
  useColumnManager,
  useSetActiveSidebarLink, 
  useSetCurrentEvent 
} from "@hooks"

import { useLazyQuery, useQuery } from "@apollo/client"
import { ClipboardDocumentCheckIcon } from "@heroicons/react/24/outline"
import { Chip } from "@heroui/react"
import { truncate } from "lodash"
import { useParams } from "react-router-dom"

import { 
  columns, 
  defaultColumns, 
  downloadOptions, 
  filterGroups,
  typeStyles 
} from "./data"

import { Stats } from "./Stats"
import { DetailsModal } from "./DetailsModal"

const AuditLog = () => {
  const { event: eventId } = useParams()
  const { user } = useContext(UserContext)
  const [currentRow, setCurrentRow] = useState({})
  const [isDownloading, setIsDownloading] = useState(false)
  const [refetching, setRefetching] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [sortDescriptor, setSortDescriptor] = useState({ column: "created_at", direction: "descending" })
  const [selectedFilterGroup, setSelectedFilterGroup] = useState([])
  const [selectedFilterIds, setSelectedFilterIds] = useState([])
  const [page, setPage] = useState(1)
  const [perPage] = useState(50)
  
  useSetActiveSidebarLink("EVENT_AUDIT_LOG", "EVENT_MANAGE")
  useSetCurrentEvent(eventId)
  const [selectedColumns, setSelectedColumns] = useColumnManager(columns, "event.auditlog.columns", defaultColumns)

  const { data: summaryData } = useQuery(GET_AUDIT_LOG_SUMMARY, { 
    fetchPolicy: "cache-and-network",
    variables: { eventId }
  })

  const {data, loading, refetch, networkStatus} = useQuery(GET_AUDIT_LOG, {
    variables: {
      eventId,
      page,
      perPage,
      orderBy: [
        {
          column: sortDescriptor.column.toUpperCase(),
          order: sortDescriptor.direction === "ascending" ? "ASC" : "DESC",
        },
      ],
      searchTerm,
      types: selectedFilterIds
    },
    fetchPolicy: "cache-and-network",
    pollInterval: 1500000,
  })

  const [getAuditLogXlsDownloadLink] = useLazyQuery(GET_AUDIT_LOG_DOWNLOAD, {
    fetchPolicy: "network-only",
    variables: { eventId },
    onCompleted: data => {
      downloadFile(data.event.auditLogXlsDownload.filename, data.event.auditLogXlsDownload.url)
      setIsDownloading(false)
    },
  })

  const getFilters = () => {
    return Object.keys(filterGroups).reduce((filters, key) => {
      const group = filterGroups[key]
      filters[key] = { id: key, name: group.title }
      return filters
    }, {})
  }

  const handleDownload = type => {
    if (type === "xls") {
      setIsDownloading(true)
      getAuditLogXlsDownloadLink()
    }
  }

  const handleRefetchButtonClicked = () => {
    setRefetching(true)
    refetch().then(() => setRefetching(false))
  }

  const handleSearchChange = searchTerm => {
    setPage(1)
    setSearchTerm(searchTerm)
  }

  const handleSearchClear = () => {
    setSearchTerm("")
    setPage(1)
  }

  const handleShowDetails = logItem => {
    setCurrentRow(logItem)
    setShowDetailsModal(true)
  }

  const handleSortChange = sortDescriptor => {
    setSortDescriptor(sortDescriptor)
    setPage(1)
  }

  const handleToggleColumn = selectedColumns => {
    setSelectedColumns(Object.keys(columns).filter(column => selectedColumns.has(column)))
  }

  const handleToggleFilter = selectedFilter => {    
    const key = selectedFilter?.currentKey
    
    setSelectedFilterIds(key && filterGroups[key] ? filterGroups[key].ids : [])
    setSelectedFilterGroup(key && filterGroups[key] ? [key] : [])
    setPage(1)
  }
  
  const renderCell = (logItem, column) => {
    if (column === "actions") {
      const items = [{ id: 1, handleClick: () => handleShowDetails(logItem), caption: "View Details" }]

      return <EllipsisDropdown items={items} />
    }

    if (["created_at"].includes(column)) {
      return mySqlToLocal(logItem[column]).format("YYYY-MM-DD HH:mm:ss")
    }

    if (column === "type") {
      return (
        <Chip classNames={{ base: `${typeStyles[logItem.type.code]}`, content: "text-white font-semibold px-3" }} size="sm" variant="flat">
          {logItem[column].description}
        </Chip>
      )
    }

    if (column === "user") {
      return logItem[column]?.name
    } 

    if (column === "details") {
      return (
        <p className="font-semibold text-green-600">
          { truncate(logItem[column], { length: 150 }) }
        </p>
      ) 
    } 

    return logItem[column]
  }

  const showLoadingPlaceholder = showLoader(loading, networkStatus, data?.event?.auditLog?.data, refetching)
  const filters = getFilters()
  const summary = summaryData?.event?.auditLogSummary
  
  return (
    <EventViewsTracker eventId={eventId}>
      <Page>
        <EventPageHeader
          title="Event Audit Log"
          icon={<ClipboardDocumentCheckIcon className="page-header-icon" />}
          event={user?.currentEvent}
          actions={<EventMenu event={user?.currentEvent} user={user} />}
        />

        <Stats summary={summary} typeStyles={typeStyles} />

        <DetailsModal details={currentRow} show={showDetailsModal} handleClose={() => setShowDetailsModal(false)} typeStyles={typeStyles} />

        <DataTable
          columns={columns}
          currentPage={page}
          currentSortOrder={sortDescriptor}
          data={data?.event?.auditLog?.data}
          disallowEmptyFilterSelection={false}
          downloadOptions={downloadOptions}
          filters={filters}
          filtersSelectionMode="single"
          handleDownload={type => handleDownload(type)}
          handlePageChange={page => setPage(page)}
          handleRefetch={() => handleRefetchButtonClicked()}
          handleRenderCell={(item, key) => renderCell(item, key)}
          handleSearchChange={searchTerm => handleSearchChange(searchTerm)}
          handleSearchClear={handleSearchClear}
          handleSortChange={sortDescriptor => handleSortChange(sortDescriptor)}
          handleToggleColumn={selectedColumns => handleToggleColumn(selectedColumns)}
          handleToggleFilter={selectedFilters => handleToggleFilter(selectedFilters)}
          loading={showLoadingPlaceholder}
          isDownloading={isDownloading}
          isRefetching={refetching}
          noDataMessage={<EmptyPlaceholder classNames="mt-2" title="No Audit Records" description="No audit records found! Try clearing any filters." />}
          paginatorInfo={data?.event?.auditLog?.paginatorInfo}
          searchInputPlaceHolder="Search by summary or user"
          searchTerm={searchTerm}
          selectedColumns={selectedColumns}
          selectedFilters={selectedFilterGroup}
          showDownloadButton={true}
          showFilterCount={true}
          showRefetchButton={true}
        />
      </Page>
    </EventViewsTracker>
  )
}

export default AuditLog